/**
 * 🚨 BLISSNET PRODUCTION-READY LOGGER - Smart Throttling & Console Hygiene
 * Only shows debug logs in development, keeps errors and warnings in production
 * Includes throttling to prevent console spam
 */

const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

// 🔇 SMART THROTTLING: Track logged messages to prevent spam
const loggedMessages = new Set<string>();
const throttledMessages = new Map<string, number>();
const THROTTLE_DURATION = 5000; // 5 seconds

/**
 * 🔇 THROTTLE HELPER: Prevent duplicate messages within time window
 */
const shouldThrottle = (key: string): boolean => {
  const now = Date.now();
  const lastLogged = throttledMessages.get(key);

  if (!lastLogged || now - lastLogged > THROTTLE_DURATION) {
    throttledMessages.set(key, now);
    return false;
  }

  return true;
};

/**
 * 🔇 LOG ONCE HELPER: Log message only once per session
 */
const logOnce = (key: string, logFn: () => void): void => {
  if (!loggedMessages.has(key)) {
    loggedMessages.add(key);
    logFn();
  }
};

class Logger {
  private static logCounts = new Map<string, number>();
  private static lastLogTimes = new Map<string, number>();

  static throttledLog(key: string, message: string, maxCount = 3, cooldown = 5000) {
    const now = Date.now();
    const lastTime = this.lastLogTimes.get(key) || 0;
    const count = this.logCounts.get(key) || 0;

    if (count < maxCount || now - lastTime > cooldown) {
      console.log(message);
      this.logCounts.set(key, count < maxCount ? count + 1 : 1);
      this.lastLogTimes.set(key, now);
    }
  }

  static cleanLog(component: string, userId?: string) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${component}]`, userId ? userId.slice(-8) : '');
    }
  }

  static info(component: string, message: string, data?: any) {
    if (process.env.NODE_ENV === 'development') {
      if (data !== undefined) {
        console.log(`[${component}] ${message}`, data);
      } else {
        console.log(`[${component}] ${message}`);
      }
    }
  }

  static warn(component: string, message: string, error?: any) {
    if (process.env.NODE_ENV === 'development') {
      if (error) {
        console.warn(`[${component}] ${message}`, error);
      } else {
        console.warn(`[${component}] ${message}`);
      }
    }
  }

  static error(component: string, message: string, error?: any) {
    if (process.env.NODE_ENV === 'development') {
      if (error) {
        console.error(`[${component}] ${message}`, error);
      } else {
        console.error(`[${component}] ${message}`);
      }
    }
  }
}

export default Logger;

// Export logger instance for backward compatibility
export const logger = {
  debug: (message: string, data?: any) => Logger.info('DEBUG', message, data),
  info: (message: string, data?: any) => Logger.info('INFO', message, data),
  warn: (message: string, error?: any) => Logger.warn('WARN', message, error),
  error: (message: string, error?: any) => Logger.error('ERROR', message, error),
  throttledLog: (key: string, message: string, maxCount = 3, cooldown = 5000) => 
    Logger.throttledLog(key, message, maxCount, cooldown),
  cleanLog: (component: string, userId?: string) => Logger.cleanLog(component, userId)
};

export const isDebugLoggingEnabled = (): boolean => {
  try {
    return import.meta.env.DEV || localStorage.getItem('blissnet_debug') === '1';
  } catch {
    return import.meta.env.DEV;
  }
};

export const debugLog = (...args: unknown[]): void => {
  if (isDebugLoggingEnabled()) {
    console.log(...args);
  }
};

export const debugInfo = (...args: unknown[]): void => {
  if (isDebugLoggingEnabled()) {
    console.info(...args);
  }
};
